import reactUpdate from 'immutability-helper';
import _ from 'underscore';

import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import {EventConstants} from 'constants/EventConstants';
import {Batch, BatchDescendantCounts, BatchViewState, BatchViewType} from 'constants/SampleConstants';
import {SchemaTag} from 'folder-items/FileConstants';
import AppDispatcher, {FluxAction} from 'lib/Dispatcher';
import Store from 'lib/Store';
import APIUtil from 'util/APIUtil';

function _viewStateForAllBatches(): BatchViewState {
  return {
    view: BatchViewType.VIEW_ALL,
    batchId: null,
    containerId: null,
    filter: '',
    createBatchModal: null,
  };
}

class SampleStore extends Store {
  batchesByFile!: {[bioentityId: string]: Array<Batch> | undefined};
  descendantCountsByBatch!: {[batchId: string]: BatchDescendantCounts};
  batchViewStates!: {[bioentityId: string]: BatchViewState | undefined};

  resetState(): void {
    this.batchesByFile = {};
    this.descendantCountsByBatch = {};
    this.batchViewStates = {};
  }

  getBatchViewState(fileId: string): BatchViewState | undefined {
    return this.batchViewStates[fileId];
  }

  getAllBatches(fileId: string): Array<Batch> | null {
    return this.batchesByFile[fileId] || null;
  }

  getNotArchivedBatches(fileId: string): Array<Batch> {
    // eslint-disable-next-line no-restricted-syntax
    return _.reject<Batch>(this.getAllBatches(fileId) || [], isArchived);
  }

  getDescendantCounts(batchId: string): BatchDescendantCounts {
    return this.descendantCountsByBatch[batchId];
  }

  _findFileIdOfBatch(batchId: string): string | null {
    // Does not assume that a batch with batchId is in the store
    for (const fileId of Object.keys(this.batchesByFile)) {
      if (_.findWhere(this.batchesByFile[fileId]!, {id: batchId})) {
        return fileId;
      }
    }
    return null;
  }

  _replaceTagsOnBatch(batchId: string, tags: Array<SchemaTag>): void {
    const fileId = this._findFileIdOfBatch(batchId);
    if (!fileId) {
      throw new Error(`Did not find file corresponding to batchId=${batchId}`);
    }
    this.batchesByFile = reactUpdate(this.batchesByFile, {
      [fileId]: {
        $set: this.batchesByFile[fileId]!.map((batch) =>
          batch.id === batchId ? reactUpdate(batch, {tags: {$set: tags}}) : batch
        ),
      },
    });
  }

  _updateBatch(updatedBatch: Batch): void {
    const fileId = this._findFileIdOfBatch(updatedBatch.id);
    assert(fileId != null, 'Did not find file corresponding to batch');
    this.batchesByFile = reactUpdate(this.batchesByFile, {
      [fileId]: {
        $set: this.batchesByFile[fileId]!.map((batch) =>
          updatedBatch.id === batch.id ? updatedBatch : batch
        ),
      },
    });
  }

  _clearBatchesWithSchema(batchSchemaId: string): void {
    this.batchesByFile = _.mapObject(this.batchesByFile, (batches) => {
      // Clears batches so they will be refetched. All batches of the same file have the same schema.
      if (batches && batches.length && batches[0].batchSchemaId === batchSchemaId) {
        return undefined;
      }
      return batches;
    });
  }

  /**
   * Blow away batches for any affected files. This forces any open BatchView components to re-fetch batches,
   * which keeps everything in sync. Any files that aren't open will just cause a normal load when the tab is
   * opened. This is both simpler and in large cases more efficient than having the endpoint return the
   * imported batches since the user likely won't have the tabs open for all affected files in a large
   * import.
   */
  _invalidateBatchesOnFiles(fileIds: Array<string>): void {
    const fileIdToUndefined: {[fileId: string]: undefined} = {};
    fileIds.forEach((fileId) => {
      fileIdToUndefined[fileId] = undefined;
    });
    this.batchesByFile = {...this.batchesByFile, ...fileIdToUndefined};
  }

  _invalidateBatches(batchIds: Array<string>): void {
    // eslint-disable-next-line no-restricted-syntax
    const fileIds = _.compact(_.uniq(batchIds.map((batchId) => this._findFileIdOfBatch(batchId))));
    this._invalidateBatchesOnFiles(fileIds);
  }

  payloadHandler(action: FluxAction): boolean {
    switch (action.actionType) {
      case EventConstants.BATCHES_FETCH_COMPLETED: {
        const {fileId, batches, descendantCounts} = action;
        this.batchesByFile = {
          ...this.batchesByFile,
          [fileId]: batches,
        };
        this.descendantCountsByBatch = {...this.descendantCountsByBatch, ...descendantCounts};
        const batchViewState = this.batchViewStates[fileId];
        // If the batch currently being viewed was deleted, view all samples
        if (batchViewState && batchViewState.view === BatchViewType.VIEW) {
          const viewedBatchId = batchViewState.batchId;
          if (!_.findWhere(this.batchesByFile[fileId]!, {id: viewedBatchId})) {
            this.batchViewStates = reactUpdate(this.batchViewStates, {
              [fileId]: {$set: _viewStateForAllBatches()},
            });
          }
        }

        this.emitChange();
        break;
      }

      case EventConstants.BULK_ARCHIVE_BATCHES_COMPLETED:
      case EventConstants.BULK_UNARCHIVE_BATCHES_COMPLETED: {
        const {itemIds} = action;
        this._invalidateBatches(itemIds);
        this.emitChange();
        break;
      }

      case EventConstants.BULK_ARCHIVE_FILES_COMPLETED:
      case EventConstants.BULK_UNARCHIVE_FILES_COMPLETED: {
        const itemIds: Array<string> = action.itemIds;
        this._invalidateBatchesOnFiles(itemIds.filter((itemId) => APIUtil.supportsBatches(itemId)));
        this.emitChange();
        break;
      }

      case EventConstants.BATCHES_BULK_UPDATE_COMPLETED:
      case EventConstants.BATCHES_IMPORT_COMPLETED: {
        const {fileIds} = action;
        this._invalidateBatchesOnFiles(fileIds);
        this.emitChange();
        break;
      }

      case EventConstants.CONTAINERS_TRANSFER_COMPLETED:
      case EventConstants.CONTAINERS_TRANSFER_UNDO_COMPLETED:
      case EventConstants.CONTAINER_CONTENTS_DELETE_COMPLETED: {
        const {invalidatableBatchIds} = action;
        this._invalidateBatches(invalidatableBatchIds);
        this.emitChange();
        break;
      }

      case EventConstants.REGISTRY_VALIDATOR_MOVE_COMPLETED: {
        const {fileIds} = action;
        this._invalidateBatchesOnFiles(fileIds);
        this.emitChange();
        break;
      }

      default:
        break;
    }

    return true;
  }
}

const sampleStore = new SampleStore(AppDispatcher);
export default sampleStore;
